import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMovies, useTVShows } from '../hooks/useContentQueries';
import { useHomepageContent } from '../hooks/useHomepageContent';

import { ContentCard, PersonSummaryWithPhoto } from '../types';
import HeroSection from '../components/HeroSection';
import ContentCarousel from '../components/ContentCarousel';
import PopularMoviesCarousel from '../components/PopularMoviesCarousel';
import { navigateToContent, navigateToPerson, hasPersonDetailPage } from '../utils/navigation';

// Utility function to randomly shuffle an array
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  // Use the homepage content hook for filtered content
  const {
    topPopularIds,
    filteredFeaturedContent,
    filteredTVShows,
    isLoading,
    error,
    sections,
    refetchAll,
  } = useHomepageContent();
  
  // Genre-based queries with popularity filtering
  const actionMoviesQuery = useMovies(1, 50, { 
    genre: 'Action', 
    sortBy: 'popularity',
    popularTitleIds: topPopularIds 
  });
  const dramaMoviesQuery = useMovies(1, 50, { 
    genre: 'Drama', 
    sortBy: 'popularity',
    popularTitleIds: topPopularIds 
  });
  const comedyMoviesQuery = useMovies(1, 50, { 
    genre: 'Comedy', 
    sortBy: 'popularity',
    popularTitleIds: topPopularIds 
  });
  const actionTVShowsQuery = useTVShows(1, 50, { 
    genre: 'Action', 
    sortBy: 'popularity',
    popularTitleIds: topPopularIds 
  });
  const dramaTVShowsQuery = useTVShows(1, 50, { 
    genre: 'Drama', 
    sortBy: 'popularity',
    popularTitleIds: topPopularIds 
  });
  const comedyTVShowsQuery = useTVShows(1, 50, { 
    genre: 'Comedy', 
    sortBy: 'popularity',
    popularTitleIds: topPopularIds 
  });
  
  // Decade-based queries with popularity filtering
  const movies2000sQuery = useMovies(1, 50, { 
    yearFrom: 2000, 
    yearTo: 2009, 
    sortBy: 'popularity',
    popularTitleIds: topPopularIds 
  });
  const movies2010sQuery = useMovies(1, 50, { 
    yearFrom: 2010, 
    yearTo: 2019, 
    sortBy: 'popularity',
    popularTitleIds: topPopularIds 
  });
  const movies2020sQuery = useMovies(1, 50, { 
    yearFrom: 2020, 
    yearTo: 2025, // Changed from 2029 to 2025 (current year)
    sortBy: 'popularity', // Use popularity sort, then sort by year on frontend
    popularTitleIds: topPopularIds 
  });



  const handleContentClick = (content: ContentCard) => {
    navigateToContent(navigate, content);
  };

  const handlePlayClick = (content: ContentCard) => {
    navigateToContent(navigate, content);
  };

  const handlePersonClick = (person: PersonSummaryWithPhoto) => {
    // Check if person has a detail page available
    if (hasPersonDetailPage(person)) {
      navigateToPerson(navigate, person);
    }
  };

  // Get the first filtered featured content item for hero section
  const heroContent = filteredFeaturedContent?.[0];

  return (
    <div className="min-h-screen bg-gray-900">


      {/* Hero Section */}
      <HeroSection 
        featuredContent={heroContent}
        onPlayClick={handlePlayClick}
      />

      {/* Content Sections */}
      <div className="space-y-8 py-8">
        {/* Featured Content Carousel - Only show top 500 popular titles, randomly sorted */}
        {filteredFeaturedContent && filteredFeaturedContent.length > 1 && (
          <ContentCarousel
            title="Recommended Titles"
            content={shuffleArray(filteredFeaturedContent.slice(1))} // Skip first item used in hero, then shuffle
            onContentClick={handleContentClick}
            onPersonClick={handlePersonClick}
          />
        )}

        {/* Recent Releases - Moved to top priority position */}
        {movies2020sQuery.data?.data && movies2020sQuery.data.data.length > 0 && (
          <ContentCarousel
            title="Recent Releases"
            content={movies2020sQuery.data.data.sort((a, b) => (b.year || 0) - (a.year || 0))}
            onContentClick={handleContentClick}
            onPersonClick={handlePersonClick}
          />
        )}

        {/* Top 100 Popular Movies Carousel - Keep original popularity order */}
        <PopularMoviesCarousel
          onContentClick={handleContentClick}
          onPersonClick={handlePersonClick}
        />

        {/* Shows Section - Filtered to top 500 popular, randomly sorted */}
        {filteredTVShows && filteredTVShows.length > 0 && (
          <ContentCarousel
            title="Popular Shows"
            content={shuffleArray(filteredTVShows).slice(0, 20)} // Shuffle then limit display to 20 items
            onContentClick={handleContentClick}
            onPersonClick={handlePersonClick}
          />
        )}

        {/* Genre-based Movie Carousels - Randomly sorted */}
        {actionMoviesQuery.data?.data && actionMoviesQuery.data.data.length > 0 && (
          <ContentCarousel
            title="Action Movies"
            content={shuffleArray(actionMoviesQuery.data.data)}
            onContentClick={handleContentClick}
            onPersonClick={handlePersonClick}
          />
        )}

        {dramaMoviesQuery.data?.data && dramaMoviesQuery.data.data.length > 0 && (
          <ContentCarousel
            title="Drama Movies"
            content={shuffleArray(dramaMoviesQuery.data.data)}
            onContentClick={handleContentClick}
            onPersonClick={handlePersonClick}
          />
        )}

        {comedyMoviesQuery.data?.data && comedyMoviesQuery.data.data.length > 0 && (
          <ContentCarousel
            title="Comedy Movies"
            content={shuffleArray(comedyMoviesQuery.data.data)}
            onContentClick={handleContentClick}
            onPersonClick={handlePersonClick}
          />
        )}

        {/* Genre-based TV Show Carousels - Randomly sorted */}
        {actionTVShowsQuery.data?.data && actionTVShowsQuery.data.data.length > 0 && (
          <ContentCarousel
            title="Action Shows"
            content={shuffleArray(actionTVShowsQuery.data.data)}
            onContentClick={handleContentClick}
            onPersonClick={handlePersonClick}
          />
        )}

        {dramaTVShowsQuery.data?.data && dramaTVShowsQuery.data.data.length > 0 && (
          <ContentCarousel
            title="Drama Shows"
            content={shuffleArray(dramaTVShowsQuery.data.data)}
            onContentClick={handleContentClick}
            onPersonClick={handlePersonClick}
          />
        )}

        {comedyTVShowsQuery.data?.data && comedyTVShowsQuery.data.data.length > 0 && (
          <ContentCarousel
            title="Comedy Shows"
            content={shuffleArray(comedyTVShowsQuery.data.data)}
            onContentClick={handleContentClick}
            onPersonClick={handlePersonClick}
          />
        )}

        {/* Decade-based Movie Collections - Randomly sorted */}
        {movies2000sQuery.data?.data && movies2000sQuery.data.data.length > 0 && (
          <ContentCarousel
            title="2000s Classics"
            content={shuffleArray(movies2000sQuery.data.data)}
            onContentClick={handleContentClick}
            onPersonClick={handlePersonClick}
          />
        )}

        {movies2010sQuery.data?.data && movies2010sQuery.data.data.length > 0 && (
          <ContentCarousel
            title="2010s Favorites"
            content={shuffleArray(movies2010sQuery.data.data)}
            onContentClick={handleContentClick}
            onPersonClick={handlePersonClick}
          />
        )}



        {/* Loading States */}
        {isLoading && (
          <div className="space-y-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="px-4 md:px-8">
                <div className="h-6 bg-gray-700 rounded w-48 mb-4 animate-pulse"></div>
                <div className="flex gap-4 overflow-hidden">
                  {[1, 2, 3, 4, 5, 6].map((j) => (
                    <div key={j} className="flex-none w-48 sm:w-56 md:w-64">
                      <div className="bg-gray-800 rounded-lg h-64 sm:h-72 md:h-80 animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error States */}
        {!isLoading && error && (
          <div className="text-center py-16">
            <div className="text-gray-400 text-lg mb-4">
              Unable to load popular content at this time
            </div>
            <div className="text-red-400 text-sm mb-4 max-w-md mx-auto">
              <div>Error: {error.message}</div>
              {sections.popular.error && <div>Popular Content: {String(sections.popular.error)}</div>}
              {sections.featured.error && <div>Featured: {String(sections.featured.error)}</div>}
              {sections.movies.error && <div>Movies: {String(sections.movies.error)}</div>}
              {sections.tvShows.error && <div>TV Shows: {String(sections.tvShows.error)}</div>}
            </div>
            <button 
              onClick={refetchAll}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        )}

        {/* No Content State */}
        {!isLoading && !error && topPopularIds.size === 0 && (
          <div className="text-center py-16">
            <div className="text-gray-400 text-lg mb-4">
              No popular content available at this time
            </div>
            <button 
              onClick={refetchAll}
              className="btn-primary"
            >
              Refresh Content
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;